import { useEffect, useMemo, useState, type KeyboardEvent } from "react"
import { router } from "@inertiajs/react"
import { Lock, Trash2, Pencil } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import type { Topic } from "@/types/topic"

type Props = {
  topic: Topic | null              // null when creating a new draft
  agentId: number                  // currently-selected agent
  tagSuggestions: string[]         // distinct tags across this agent's catalog
  isDraft: boolean
  onSaved: () => void
  onCancel: () => void
  onDelete: () => void
}

const BLANK = { name: "", key: "", description: "" }
const DESCRIPTION_MAX = 120

function slugify(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
}
function normalizeTag(input: string): string {
  return input.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "")
}

export function TopicForm({ topic, agentId, tagSuggestions, isDraft, onSaved, onCancel, onDelete }: Props) {
  const [fields, setFields]       = useState(BLANK)
  const [submitting, setSubmitting] = useState(false)
  const [keyTouched, setKeyTouched] = useState(false)
  const [tags, setTags]             = useState<string[]>([])
  const [tagDraft, setTagDraft]     = useState("")

  useEffect(() => {
    if (topic) {
      setFields({ name: topic.name, key: topic.key, description: topic.description ?? "" })
      setKeyTouched(true)
      setTags(Array.isArray(topic.tags) ? [...topic.tags] : [])
    } else {
      setFields(BLANK)
      setKeyTouched(false)
      setTags([])
    }
    setTagDraft("")
  }, [topic?.id, isDraft])

  const keyImmutable = !isDraft && !!topic?.keyImmutable

  const addTag = (raw: string) => {
    const t = normalizeTag(raw)
    if (!t || tags.includes(t)) { setTagDraft(""); return }
    setTags((prev) => [...prev, t])
    setTagDraft("")
  }
  const removeTag = (t: string) => setTags((prev) => prev.filter((x) => x !== t))

  const handleNameChange = (val: string) => {
    setFields((f) => ({ ...f, name: val, key: keyTouched ? f.key : slugify(val) }))
  }

  const onTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      if (tagDraft.trim()) addTag(tagDraft)
    } else if (e.key === "Backspace" && tagDraft === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  const filteredSuggestions = useMemo(() => {
    const q = tagDraft.trim().toLowerCase()
    if (!q) return []
    return tagSuggestions.filter((s) => s.includes(q) && !tags.includes(s)).slice(0, 6)
  }, [tagDraft, tagSuggestions, tags])

  const previewText = fields.name
    ? `${fields.name}${fields.description ? ` — ${fields.description}` : ""}`
    : "(name required)"

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const payload = {
      topic: {
        agent_id: agentId,
        name: fields.name, key: fields.key, description: fields.description,
        tags,
      },
    }
    const onFinish = () => { setSubmitting(false); onSaved() }
    if (isDraft || !topic) {
      router.post("/topics", payload, { preserveScroll: true, onFinish })
    } else {
      router.patch(`/topics/${topic.id}`, payload, { preserveScroll: true, onFinish })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
      <Card className="flex h-full min-h-0 flex-col p-0">
        <header className="border-b border-border p-4">
          <div className="flex items-center gap-2">
            <Pencil size={16} strokeWidth={2} className="text-app-muted" />
            <h2 className="text-base font-semibold text-app-text">{isDraft ? "New topic" : "Edit topic"}</h2>
          </div>
          <p className="mt-1 text-xs text-app-muted">
            {isDraft
              ? "Authoring a new label for this agent's catalog."
              : "Update topic details and metadata."}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            <Section number={1} title="Basics">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>Name</Label>
                  <input
                    type="text" required value={fields.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Payouts & cashout"
                    className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-app-muted">Shown in the help menu and in admin lists.</p>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <Label>Key (slug)</Label>
                    {keyImmutable && (
                      <span className="inline-flex items-center gap-1 rounded-control border border-vip-gold/40 bg-vip-goldMute px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-vip-goldSoft">
                        <Lock size={10} strokeWidth={2} /> Locked
                      </span>
                    )}
                  </div>
                  <input
                    type="text" required value={fields.key}
                    onChange={(e) => { setKeyTouched(true); setFields((f) => ({ ...f, key: e.target.value })) }}
                    disabled={keyImmutable}
                    pattern="[a-z0-9_]+"
                    placeholder="payouts_cashout"
                    className="w-full rounded-control border border-border bg-app-bg px-3 py-2 font-mono text-sm text-app-text focus:border-brand-red focus:outline-none disabled:cursor-not-allowed disabled:bg-app-panel disabled:text-app-muted"
                  />
                  <p className="mt-1 text-xs text-app-muted">
                    {keyImmutable
                      ? "Slug locked. Delete and recreate to rename."
                      : "Lowercase letters, digits, underscores."}
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <Label>Description</Label>
                  <span className="text-[11px] text-app-muted">{fields.description.length} / {DESCRIPTION_MAX}</span>
                </div>
                <textarea
                  rows={2} maxLength={DESCRIPTION_MAX} value={fields.description}
                  onChange={(e) => setFields((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Cashing out, payout statuses, denials."
                  className="w-full resize-y rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
                />
                <p className="mt-1 text-xs italic text-app-muted">
                  Preview: <span className="not-italic text-app-text">{previewText}</span>
                </p>
              </div>
            </Section>

            <Section number={2} title="Tags">
              <div className="space-y-3">
                <p className="text-xs text-app-muted">
                  Free-form labels for grouping, filtering, and operator context.
                </p>

                <div>
                  <Label>
                    Tags
                    <InfoTooltip side="bottom">
                      Free-form labels. The platform stores them as topic metadata.
                    </InfoTooltip>
                  </Label>
                  <div className="flex flex-wrap items-center gap-1.5 rounded-control border border-border bg-app-bg p-2">
                    {tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 rounded-control border border-border bg-app-panel px-2 py-0.5 text-xs text-app-text">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} aria-label={`Remove ${t}`} className="opacity-70 transition hover:opacity-100">✕</button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={tagDraft}
                      onChange={(e) => setTagDraft(e.target.value)}
                      onKeyDown={onTagKeyDown}
                      onBlur={() => tagDraft.trim() && addTag(tagDraft)}
                      placeholder={tags.length === 0 ? "Add tags (Enter or , to add)" : ""}
                      className="min-w-[120px] flex-1 bg-transparent px-1 py-0.5 text-sm text-app-text placeholder:text-app-muted focus:outline-none"
                    />
                  </div>
                  {filteredSuggestions.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="text-[11px] text-app-muted">Suggestions:</span>
                      {filteredSuggestions.map((s) => (
                        <button key={s} type="button" onClick={() => addTag(s)}
                          className="rounded-control border border-border bg-app-bg px-1.5 py-0.5 text-[11px] text-app-muted transition hover:border-brand-red hover:text-app-text">
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Section>

          </div>
        </div>

        <footer className="flex items-center justify-between border-t border-border p-4">
          <div>
            {!isDraft && topic && (
              <Button type="button" variant="ghost" onClick={onDelete} className="border-status-error/40 text-status-error hover:bg-status-error/10">
                <Trash2 size={14} strokeWidth={2} className="mr-1.5" /> Delete topic
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </footer>
      </Card>
    </form>
  )
}

// Numbered section header ("1 Basics", "2 Tags", …) with optional
// right-aligned caption matching the mockup.
function Section({ number, title, right, children }: {
  number: number
  title: string
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Card variant="inset" className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border bg-app-panel2 text-[11px] font-semibold text-app-muted">{number}</span>
          <h3 className="text-sm font-semibold text-app-text">{title}</h3>
        </div>
        {right}
      </div>
      <div className="space-y-3">{children}</div>
    </Card>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wide text-app-muted">
      {children}
    </div>
  )
}
